import { useId, useState } from 'react';

import { getKioskSteps } from '../../utils/goLocker';
import BagQrImage from '../../images/golocker-bag-qr.png';
import GoLockerTip from './GoLockerTip';
import GoLockerMark from './GoLockerMark';

import styles from './GoLocker.module.scss';

const NumberedStep = ({ number, children, hint }) => (
  <li className={styles.step}>
    <span className={styles.stepNumber} aria-hidden="true">{number}</span>
    <span className={styles.stepLabel}>
      {children}
      {hint}
    </span>
  </li>
);

/**
 * GoLockerKioskCard — "At the GoLocker kiosk" (Figma G3 12229:3401, E3 12258:7219,
 * E2 12258:7065).
 *
 * Full variant: logo, title, the self-service subtitle (12229:3412), five numbered kiosk
 * steps, and the one-bag-per-locker tip. Step 3 leads with the QR above; its
 * "scan the QR code on your bag" backup line toggles the bag-QR illustration
 * between steps 3 and 4 (hidden by default). Copy switches to the multi-bag
 * wording when `bagCount > 1` (E3).
 *
 * `hasQrAbove` is false when the screen shows no QR to scan: step 3 then points at
 * the bag and the illustration is always shown.
 *
 * `logoUrl` is the method's `configuration.icon` (bundled logo as fallback).
 *
 * `compact` is E2 — the labels are still generating, so there is no QR to scan yet:
 * just the reassurance and a "Check live availability" link.
 */
const GoLockerKioskCard = ({
  bagCount = 1, compact = false, findLocation, hasQrAbove = true, logoUrl,
}) => {
  const {
    before, after, note, bagHint,
  } = getKioskSteps(bagCount, hasQrAbove);
  const [expanded, setExpanded] = useState(false);
  const bagQrId = useId();
  const showBagQr = !bagHint || expanded;

  return (
    <section className={styles.kioskCard} aria-label="At the GoLocker kiosk">
      <GoLockerMark size="box" logoUrl={logoUrl} />
      <div className={styles.kioskHeading}>
        <h3 className={styles.kioskTitle}>At the GoLocker kiosk</h3>
        {compact ? (
          <p className={styles.kioskSubtitle}>
            Use any GoLocker.
            <br />
            Scan each bag separately, one bag per locker.
          </p>
        ) : (
          <p className={styles.kioskSubtitle}>
            Self-service, no staff needed.
            <br />
            Look for the GoLocker locker. Any location works
          </p>
        )}
      </div>

      {compact ? (
        findLocation && (
          <a
            className={styles.kioskLink}
            href={findLocation}
            target="_blank"
            rel="noopener noreferrer"
          >
            Check live availability
          </a>
        )
      ) : (
        <>
          <ol className={styles.steps}>
            {before.map((label, index) => (
              <NumberedStep
                key={label}
                number={index + 1}
                hint={bagHint && index === before.length - 1 && (
                  <button
                    type="button"
                    className={styles.stepHint}
                    aria-expanded={expanded}
                    aria-controls={bagQrId}
                    onClick={() => setExpanded((shown) => !shown)}
                  >
                    {bagHint}
                  </button>
                )}
              >
                {label}
              </NumberedStep>
            ))}
            {showBagQr && (
              <li id={bagQrId} className={styles.stepImage}>
                <img src={BagQrImage} alt="Where the QR code is on the back of your Take Back Bag" />
              </li>
            )}
            {after.map((label, index) => (
              <NumberedStep key={label} number={before.length + index + 1}>{label}</NumberedStep>
            ))}
          </ol>
          <GoLockerTip>{note}</GoLockerTip>
        </>
      )}
    </section>
  );
};

export default GoLockerKioskCard;
